import React, { useEffect, useMemo, useState } from "react";
import { ContentState, EditorState, convertFromRaw } from "draft-js";
import { BackButton } from "../../components/common/MainButtonComponent";
import ContentWrapper from "../../components/common/AssessmentContentWrapper";
import ViewEditor from "../../components/common/editor/ViewEditor";
import RadioInline from "../../components/common/form/RadioInline";
import Radio from "../../components/common/form/Radio";
import CheckBoxInline from "../../components/common/form/CheckBoxInline";
import CheckBox from "../../components/common/form/CheckBox";
import Button from "../../components/common/form/Button";
import { AzureStorage } from "../../helper/azureStorage";
import config from "../../helper/config";
import { createService } from "../../service/question/createService";
import { libraryService } from "../../service/question/libraryService";
import "./AssessShared.css";
import "./SetupAssessment.css";

/*
|--------------------------------------------------------------------------
| General display helpers
|--------------------------------------------------------------------------
*/

const getDisplayText = (value, fallback = "N/A") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : fallback;
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
};

const formatCreatedAt = (value) => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  const normalizedValue = String(value).replace(/\.(\d{3})\d+/, ".$1");
  const parsedDate = new Date(normalizedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(parsedDate);
};

/*
|--------------------------------------------------------------------------
| Draft-JS helpers
|--------------------------------------------------------------------------
*/

const parseJsonSafely = (value) => {
  if (typeof value !== "string") {
    return value;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  try {
    return JSON.parse(trimmedValue);
  } catch {
    return value;
  }
};

const normalizeRawContentList = (value) => {
  const parsedValue = parseJsonSafely(value);

  if (!parsedValue) {
    return [];
  }

  if (Array.isArray(parsedValue)) {
    return parsedValue;
  }

  if (parsedValue?.blocks && Array.isArray(parsedValue.blocks)) {
    return [parsedValue];
  }

  return [];
};

const createEditorState = (value) => {
  const rawContentList = normalizeRawContentList(value);
  const rawContent = rawContentList[0];

  if (rawContent?.blocks && Array.isArray(rawContent.blocks)) {
    try {
      return EditorState.createWithContent(convertFromRaw(rawContent));
    } catch (error) {
      console.error("Unable to convert Draft-JS content:", error);
    }
  }

  if (
    typeof value === "string" &&
    value.trim() &&
    !value.trim().startsWith("[") &&
    !value.trim().startsWith("{")
  ) {
    return EditorState.createWithContent(
      ContentState.createFromText(value)
    );
  }

  return null;
};

const editorStateHasContent = (editorState) =>
  Boolean(editorState?.getCurrentContent?.().hasText?.()) ||
  Boolean(
    editorState
      ?.getCurrentContent?.()
      ?.getBlockMap?.()
      ?.some?.((block) => block.getEntityAt?.(0))
  );

/*
|--------------------------------------------------------------------------
| Question-type helpers
|--------------------------------------------------------------------------
*/

const getQuestionTypeFlags = (question) => {
  const qtype = Number(
    question?.qtype ??
      question?.raw?.qtype ??
      question?.question_type_id
  );

  const typeName = String(
    question?.type ??
      question?.question_type_name ??
      question?.raw?.question_type_name ??
      ""
  )
    .trim()
    .toLowerCase();

  return {
    isMCQ: qtype === 1 || typeName === "mcq",
    isDescriptive:
      qtype === 2 || typeName.includes("descriptive"),
    isSubmission:
      qtype === 3 || typeName.includes("submission"),
    isSubQuestion:
      qtype === 5 ||
      typeName === "sub question" ||
      typeName === "subquestion",
  };
};

/*
|--------------------------------------------------------------------------
| MCQ helpers
|--------------------------------------------------------------------------
*/

const getMcqEditorStates = (content) => {
  const rawContentList = normalizeRawContentList(content);

  return rawContentList
    .map((rawContent) => {
      try {
        return EditorState.createWithContent(
          convertFromRaw(rawContent)
        );
      } catch (error) {
        console.error("Unable to convert MCQ content:", error);
        return null;
      }
    })
    .filter(Boolean);
};

const getSelectedChoices = (answer, choiceCount) => {
  const parsedAnswer = parseJsonSafely(answer);

  let values = [];

  if (Array.isArray(parsedAnswer)) {
    values = parsedAnswer;
  } else if (typeof parsedAnswer === "string") {
    values = parsedAnswer
      .replace(/^"|"$/g, "")
      .split(",")
      .map((item) => item.trim());
  } else if (
    parsedAnswer !== null &&
    parsedAnswer !== undefined
  ) {
    values = [parsedAnswer];
  }

  return Array.from({ length: choiceCount }, (_, index) => {
    const value = values[index];

    return (
      value === true ||
      value === 1 ||
      value === "1" ||
      value === 100 ||
      value === "100" ||
      String(value).toLowerCase() === "true"
    );
  });
};

/*
|--------------------------------------------------------------------------
| Submission attachment helpers
|--------------------------------------------------------------------------
*/

const normalizeAttachments = (question) => {
  const attachmentSources = [
    question?.attachments,
    question?.attachment,
    question?.raw?.attachments,
    question?.raw?.attachment,
    question?.answer,
    question?.raw?.answer,
  ];

  const attachmentValue = attachmentSources.find((value) => {
    if (value === null || value === undefined || value === "") {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return true;
  });

  const parsedValue = parseJsonSafely(attachmentValue);

  if (Array.isArray(parsedValue)) {
    return parsedValue
      .map((item) => {
        if (typeof item === "string") {
          return item.trim();
        }

        return (
          item?.file_name ??
          item?.filename ??
          item?.name ??
          item?.blob_name ??
          null
        );
      })
      .filter(Boolean);
  }

  if (parsedValue && typeof parsedValue === "object") {
    const filename =
      parsedValue.file_name ??
      parsedValue.filename ??
      parsedValue.name ??
      parsedValue.blob_name;

    return filename ? [filename] : [];
  }

  if (
    typeof parsedValue === "string" &&
    parsedValue.trim() &&
    !parsedValue.includes('"blocks"')
  ) {
    return parsedValue
      .replace(/^\[|\]$/g, "")
      .replace(/^"|"$/g, "")
      .split(",")
      .map((item) => item.replace(/^"|"$/g, "").trim())
      .filter(Boolean);
  }

  return [];
};
/*
|--------------------------------------------------------------------------
| Setup question view model
|--------------------------------------------------------------------------
*/

const normalizeQuestionViewModel = (question, questionId) => {
  const normalized = question || {};
  const raw = normalized.raw || {};

  return {
    id:
      normalized.questionId ??
      normalized.question_id ??
      normalized.id ??
      raw.question_id ??
      questionId ??
      null,

    code:
      normalized.code ??
      normalized.question_code ??
      normalized.title ??
      raw.title ??
      `Q${
        normalized.questionId ??
        normalized.question_id ??
        normalized.id ??
        questionId ??
        ""
      }`,

    name:
      normalized.name ??
      normalized.title ??
      normalized.question_title ??
      raw.title ??
      "Question",

    qtype:
      normalized.qtype ??
      normalized.question_type_id ??
      raw.qtype ??
      raw.question_type_id ??
      null,

    question_type_name:
      normalized.question_type_name ??
      normalized.type ??
      raw.question_type_name ??
      raw.type ??
      "",
    content:
      normalized.content ??
      normalized.question_content ??
      raw.content ??
      "",

    answer:
      normalized.answer ??
      normalized.modelAnswer ??
      normalized.model_answer ??
      raw.answer ??
      "",

    answer_type_id:
      normalized.answer_type_id ??
      raw.answer_type_id ??
      null,

    is_passage:
      normalized.is_passage ??
      raw.is_passage ??
      false,

    passage_content:
      normalized.passage_content ??
      raw.passage_content ??
      "",

    attachments:
      normalized.attachments ??
      raw.attachments ??
      [],

    sub_questions_details: Array.isArray(
      normalized.sub_questions_details
    )
      ? normalized.sub_questions_details
      : Array.isArray(raw.sub_questions_details)
      ? raw.sub_questions_details
      : [],

    raw,
  };
};

/*
|--------------------------------------------------------------------------
| Reusable view components
|--------------------------------------------------------------------------
*/

function QuestionViewBlock({ title, children }) {
  return (
    <div className="setup-question-view-block">
      <h2>{title}:</h2>
      <div className="setup-question-view-rich-content">
        {children}
      </div>
    </div>
  );
}

function EmptyContent({ children }) {
  return (
    <p className="setup-question-view-empty">
      {children}
    </p>
  );
}

/*
|--------------------------------------------------------------------------
| Main component
|--------------------------------------------------------------------------
*/

export default function SetupAssessmentQuestionViewPage({
  setOpenQuestionView,
  question: providedQuestion,
}) {
  const question = useMemo(
    () => normalizeQuestionViewModel(providedQuestion),
    [providedQuestion]
  );

  const {
    isMCQ,
    isDescriptive,
    isSubmission,
    isSubQuestion,
  } = getQuestionTypeFlags(question);

  const isIndividualSubQuestion =
    isSubQuestion && question.sub_questions_details.length === 0;

  const passageEditorState = useMemo(
    () => createEditorState(question.passage_content),
    [question.passage_content]
  );

  const normalQuestionEditorState = useMemo(
    () => createEditorState(question.content),
    [question.content]
  );

  const descriptiveAnswerEditorState = useMemo(
    () => createEditorState(question.answer),
    [question.answer]
  );

  const mcqEditorStates = useMemo(
    () => (isMCQ ? getMcqEditorStates(question.content) : []),
    [isMCQ, question.content]
  );

  const mcqQuestionEditorState = mcqEditorStates[0] ?? null;
  const mcqChoiceEditorStates = mcqEditorStates.slice(1);

  const selectedChoices = useMemo(
    () =>
      getSelectedChoices(
        question.answer,
        mcqChoiceEditorStates.length
      ),
    [question.answer, mcqChoiceEditorStates.length]
  );

  const submissionAttachments = useMemo(
    () => (isSubmission ? normalizeAttachments(question) : []),
    [isSubmission, question]
  );

  const normalizedSubQuestions = useMemo(
    () =>
      question.sub_questions_details.map((subQuestion, index) => {
        const normalizedSubQuestion =
          normalizeQuestionViewModel(subQuestion);

        return {
          ...normalizedSubQuestion,
          index,
          contentEditorState: createEditorState(
            normalizedSubQuestion.content
          ),
          answerEditorState: createEditorState(
            normalizedSubQuestion.answer
          ),
        };
      }),
    [question.sub_questions_details]
  );

  const goBack = () => {
    if (typeof setOpenQuestionView === "function") {
      setOpenQuestionView(null);
    }
  };

  const onDownloadAttachment = async (attachment) => {
    try {
      const containerName =
        config.storageBlobConfigs.containerName;

      const content = await AzureStorage.downloadBlob(
        containerName,
        attachment
      );

      const url = window.URL.createObjectURL(content);
      const link = document.createElement("a");

      link.href = url;
      link.download = attachment;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Unable to download attachment:", error);
    }
  };

  const displayedQuestionEditorState = isMCQ
    ? mcqQuestionEditorState
    : normalQuestionEditorState;

  return (
    <ContentWrapper>
      <section className="assess-section setup-question-view-page">
        <div className="setup-meta-page__actions">
          <BackButton label="Back" onClick={goBack} />
        </div>

        <div className="setup-question-view-card">
          <div className="setup-question-view-card__header">
            <div>
              <span>Question Details</span>
              <h1>
                {question.name ||
                  question.code ||
                  question.id}
              </h1>
            </div>
          </div>

          <div className="setup-question-view-card__body">
            {question.is_passage &&
              editorStateHasContent(passageEditorState) && (
                <QuestionViewBlock title="Passage">
                  <ViewEditor
                    editorState={passageEditorState}
                  />
                </QuestionViewBlock>
              )}

            <QuestionViewBlock
              title={isIndividualSubQuestion ? "Sub Question" : "Question"}
            >
              {editorStateHasContent(
                displayedQuestionEditorState
              ) ? (
                <ViewEditor
                  editorState={displayedQuestionEditorState}
                />
              ) : (
                <EmptyContent>
                  No question content available.
                </EmptyContent>
              )}
            </QuestionViewBlock>

            {isMCQ && (
              <QuestionViewBlock title="Model Answer">
                {mcqChoiceEditorStates.length > 0 ? (
                  mcqChoiceEditorStates.map(
                    (choiceEditorState, index) => (
                      <div
                         className="view-questions-answer d-flex align-items-center setup-mcq-answer-row"
                        key={index}
                      >
                        {Number(question.answer_type_id) === 1 ? (
                          <RadioInline className="setup-mcq-answer-control">
                            <Radio
                              checked={Boolean(
                                selectedChoices[index]
                              )}
                              className="oatlib-control-accent"
                              readOnly
                            />
                          </RadioInline>
                        ) : (
                          <CheckBoxInline className="setup-mcq-answer-control">
                            <CheckBox
                              checked={Boolean(
                                selectedChoices[index]
                              )}
                              className="oatlib-control-accent"
                              readOnly
                            />
                          </CheckBoxInline>
                        )}

                        <div className="RichEditor-root flex-grow-1">
                          <ViewEditor
                            editorState={choiceEditorState}
                          />
                        </div>
                      </div>
                    )
                  )
                ) : (
                  <EmptyContent>
                    No answer choices available.
                  </EmptyContent>
                )}
              </QuestionViewBlock>
            )}

            {(isDescriptive || isIndividualSubQuestion) && (
              <QuestionViewBlock title="Model Answer">
                {editorStateHasContent(
                  descriptiveAnswerEditorState
                ) ? (
                  <ViewEditor
                    editorState={descriptiveAnswerEditorState}
                  />
                ) : (
                  <EmptyContent>
                    Model answer not available.
                  </EmptyContent>
                )}
              </QuestionViewBlock>
            )}
            {isSubmission && (
              <QuestionViewBlock title="Model Answer">
                {submissionAttachments.length > 0 ? (
                  submissionAttachments.map((attachment) => (
                    <div
                      className="mb-2"
                      key={attachment}
                    >
                      <Button
                        className="btn btn-outline-primary primary-button"
                        btnIconStyle="flaticon-interface-3"
                        btnName={attachment}
                        onClick={() =>
                          onDownloadAttachment(attachment)
                        }
                      />
                    </div>
                  ))
                ) : (
                  <EmptyContent>
                    No Model Answer.
                  </EmptyContent>
                )}
              </QuestionViewBlock>
            )}

            {isSubQuestion &&
              normalizedSubQuestions.length > 0 && (
                <QuestionViewBlock title="Sub Questions">
                  <div className="setup-question-view-subquestions">
                    {normalizedSubQuestions.map(
                      (subQuestion, index) => (
                        <div
                          className="setup-question-view-subquestion"
                          key={
                            subQuestion.id ??
                            `sub-question-${index}`
                          }
                        >
                          <div className="row">
                            <div className="col-12 col-lg-6">
                              <h3>
                                Sub Question{" "}
                                {String.fromCharCode(97 + index)}
                              </h3>

                              {editorStateHasContent(
                                subQuestion.contentEditorState
                              ) ? (
                                <ViewEditor
                                  editorState={
                                    subQuestion.contentEditorState
                                  }
                                />
                              ) : (
                                <EmptyContent>
                                  No subquestion content
                                  available.
                                </EmptyContent>
                              )}
                            </div>

                            <div className="col-12 col-lg-6">
                              <h3>Model Answer</h3>

                              {editorStateHasContent(
                                subQuestion.answerEditorState
                              ) ? (
                                <ViewEditor
                                  editorState={
                                    subQuestion.answerEditorState
                                  }
                                />
                              ) : (
                                <EmptyContent>
                                  No model answer available.
                                </EmptyContent>
                              )}
                            </div>
                          </div>

                        </div>
                      )
                    )}
                  </div>
                </QuestionViewBlock>
              )}

          </div>
        </div>
      </section>
    </ContentWrapper>
  );
}
